import { Arrays, DefaultObservableValue, isDefined, isNull, Notifier, Option, SetMultimap, SyncStream, Terminator, UUID } from "@opendaw/lib-std";
import { RenderQuantum } from "@opendaw/lib-dsp";
import { Communicator, Messenger } from "@opendaw/lib-runtime";
import { EngineSettingsSchema, EngineStateSchema, ExportConfiguration, PERF_BUFFER_SIZE, PreferencesHost } from "@opendaw/studio-adapters";
import { AnimationFrame } from "@opendaw/lib-dom";
import { EngineVariant } from "./EngineVariant";
import { MonitoringRouter } from "./MonitoringRouter";
import { MIDIReceiver } from "./midi";
import { HRClockWorker } from "./HRClockWorker";
export class EngineWorklet extends AudioWorkletNode {
    static ID = 0 | 0;
    id = EngineWorklet.ID++;
    #terminator = new Terminator();
    #project;
    #playbackTimestamp = new DefaultObservableValue(0.0);
    #position = new DefaultObservableValue(0.0);
    #bpm = new DefaultObservableValue(120.0);
    #isPlaying = new DefaultObservableValue(false);
    #isRecording = new DefaultObservableValue(false);
    #isCountingIn = new DefaultObservableValue(false);
    #countInBeatsRemaining = new DefaultObservableValue(0);
    #preferences;
    #markerState = new DefaultObservableValue(null);
    #cpuLoad = new DefaultObservableValue(0);
    #controlFlags;
    #notifyClipNotification;
    #notifyNoteSignals;
    #playingClips;
    #deviceMessageListeners = new SetMultimap();
    #commands;
    #frozenAudioWriter;
    #isReady;
    #perfBuffer = new Float32Array(0);
    #perfIndex = 0;
    #lastPerfReadIndex = 0;
    #consecutiveOverloadCount = 0;
    #lastCpuLoadUpdate = 0;
    #maxMsSinceLastUpdate = 0;
    #monitoringRouter = null;
    constructor(context, project, exportConfiguration, options) {
        const numberOfChannels = ExportConfiguration.countStems(Option.wrap(exportConfiguration)) * 2;
        const budgetMs = (RenderQuantum / context.sampleRate) * 1000;
        const reader = SyncStream.reader(EngineStateSchema(), state => {
            this.#isPlaying.setValue(state.isPlaying);
            this.#isRecording.setValue(state.isRecording);
            this.#isCountingIn.setValue(state.isCountingIn);
            this.#countInBeatsRemaining.setValue(state.countInBeatsRemaining);
            this.#playbackTimestamp.setValue(state.playbackTimestamp);
            this.#bpm.setValue(state.bpm);
            this.#perfBuffer = state.perfBuffer;
            this.#perfIndex = state.perfIndex;
            this.#updateCpuLoad(budgetMs, project);
            this.#position.setValue(state.position); // This must be the last to handle the state values before
        });
        const controlFlagsSAB = new SharedArrayBuffer(4); // 4 bytes minimum
        // The engine is ALWAYS the installed variant (the wasm engine). There is no built-in fallback
        // processor to drop to, so an absent provider is a boot error rather than a silent downgrade.
        const variant = EngineVariant.current();
        super(context, variant.processorName, {
            numberOfInputs: 1,
            numberOfOutputs: 2,
            outputChannelCount: [numberOfChannels, 8],
            processorOptions: {
                syncStreamBuffer: reader.buffer,
                controlFlagsBuffer: controlFlagsSAB,
                hrClockBuffer: HRClockWorker.get().sab,
                project: project.toArrayBuffer(),
                exportConfiguration,
                options,
                variant: variant.attachment
            }
        });
        const { resolve, promise: isReady } = Promise.withResolvers();
        const messenger = Messenger.for(this.port);
        this.#project = project;
        this.#isReady = isReady;
        this.#notifyClipNotification = this.#terminator.own(new Notifier());
        this.#notifyNoteSignals = this.#terminator.own(new Notifier());
        this.#playingClips = [];
        this.#controlFlags = new Int32Array(controlFlagsSAB);
        this.#commands = this.#terminator.own(Communicator.sender(messenger.channel("engine-commands"), dispatcher => new class {
            play() { dispatcher.dispatchAndForget(this.play); }
            stop(reset) { dispatcher.dispatchAndForget(this.stop, reset); }
            setPosition(position) { dispatcher.dispatchAndForget(this.setPosition, position); }
            prepareRecordingState(countIn) {
                dispatcher.dispatchAndForget(this.prepareRecordingState, countIn);
            }
            stopRecording() { dispatcher.dispatchAndForget(this.stopRecording); }
            queryLoadingComplete() {
                return dispatcher.dispatchAndReturn(this.queryLoadingComplete);
            }
            panic() { dispatcher.dispatchAndForget(this.panic); }
            sleep() { dispatcher.dispatchAndForget(this.sleep); }
            wake() { dispatcher.dispatchAndForget(this.wake); }
            loadClickSound(index, data) {
                dispatcher.dispatchAndForget(this.loadClickSound, index, data);
            }
            setFrozenAudio(uuid, audioData) {
                dispatcher.dispatchAndForget(this.setFrozenAudio, uuid, audioData);
            }
            noteSignal(signal) { dispatcher.dispatchAndForget(this.noteSignal, signal); }
            ignoreNoteRegion(uuid) {
                dispatcher.dispatchAndForget(this.ignoreNoteRegion, uuid);
            }
            suspendAutomation(uuid) {
                dispatcher.dispatchAndForget(this.suspendAutomation, uuid);
            }
            scheduleClipPlay(clipIds) {
                dispatcher.dispatchAndForget(this.scheduleClipPlay, clipIds);
            }
            scheduleClipStop(trackIds) {
                dispatcher.dispatchAndForget(this.scheduleClipStop, trackIds);
            }
            setupMIDI(port, buffer) {
                dispatcher.dispatchAndForget(this.setupMIDI, port, buffer);
            }
            updateMonitoringMap(map) {
                dispatcher.dispatchAndForget(this.updateMonitoringMap, map);
            }
            terminate() { dispatcher.dispatchAndForget(this.terminate); }
        }));
        this.#frozenAudioWriter = isDefined(variant.connectFrozenAudio)
            ? variant.connectFrozenAudio(messenger) : null;
        this.#monitoringRouter = this.#terminator.own(new MonitoringRouter(this, this.#commands));
        const { port, sab } = this.#terminator.own(MIDIReceiver.create(() => context instanceof AudioContext ? context.outputLatency * 1000 : 20, (deviceId, data, relativeTimeInMs) => this.#project.receivedMIDIFromEngine(deviceId, data, relativeTimeInMs)));
        this.#commands.setupMIDI(port, sab);
        Communicator.executor(messenger.channel("engine-to-client"), {
            log: (message) => console.log("WORKLET", message),
            error: (reason) => this.dispatchEvent(new ErrorEvent("error", { error: reason })),
            deviceMessage: (uuid, message) => {
                for (const listener of this.#deviceMessageListeners.get(uuid)) {
                    listener(message);
                }
            },
            ready: () => resolve(),
            fetchAudio: (uuid) => {
                return new Promise((resolve, reject) => {
                    const handler = project.sampleManager.getOrCreate(uuid);
                    const subscription = handler.subscribe(state => {
                        if (state.type === "error") {
                            reject(new Error(state.reason));
                            subscription.terminate();
                        }
                        else if (state.type === "loaded") {
                            resolve(handler.data.unwrap("handler.data"));
                            subscription.terminate();
                        }
                    });
                });
            },
            fetchSoundfont: (uuid) => {
                return new Promise((resolve, reject) => {
                    const handler = project.soundfontManager.getOrCreate(uuid);
                    const subscription = handler.subscribe(state => {
                        if (state.type === "error") {
                            reject(new Error(state.reason));
                            subscription.terminate();
                        }
                        else if (state.type === "loaded") {
                            resolve(handler.soundfont.unwrap("handler.soundfont"));
                            subscription.terminate();
                        }
                    });
                });
            },
            fetchNamWasm: async () => {
                const url = new URL("@opendaw/nam-wasm/nam.wasm", import.meta.url);
                const response = await fetch(url);
                return response.arrayBuffer();
            },
            notifyClipSequenceChanges: (changes) => {
                changes.stopped.forEach(uuid => {
                    for (let i = 0; i < this.#playingClips.length; i++) {
                        if (UUID.equals(this.#playingClips[i], uuid)) {
                            this.#playingClips.splice(i, 1);
                            break;
                        }
                    }
                });
                changes.started.forEach(uuid => this.#playingClips.push(uuid));
                this.#notifyClipNotification.notify({ type: "sequencing", changes });
            },
            switchMarkerState: (state) => this.#markerState.setValue(state)
        });
        this.#preferences = this.#terminator.own(new PreferencesHost(EngineSettingsSchema.parse({})));
        this.#terminator.ownAll(AnimationFrame.add(() => reader.tryRead()), project.liveStreamReceiver.connect(messenger.channel("engine-live-data")), this.#preferences.syncWith(messenger.channel("engine-preferences")), variant.connectSync(messenger, project));
    }
    play() {
        this.wake();
        this.#commands.play();
    }
    stop(reset = false) { this.#commands.stop(reset); }
    setPosition(position) { this.#commands.setPosition(position); }
    prepareRecordingState(countIn) { this.#commands.prepareRecordingState(countIn); }
    stopRecording() { this.#commands.stopRecording(); }
    panic() { this.#commands.panic(); }
    sleep() {
        Atomics.store(this.#controlFlags, 0, 1);
        this.#isPlaying.setValue(false);
        this.#commands.stop(true);
    }
    wake() { Atomics.store(this.#controlFlags, 0, 0); }
    loadClickSound(index, data) { this.#commands.loadClickSound(index, data); }
    setFrozenAudio(uuid, audioData) {
        if (isNull(this.#frozenAudioWriter)) {
            this.#commands.setFrozenAudio(uuid, audioData);
        }
        else {
            this.#frozenAudioWriter(uuid, audioData);
        }
    }
    get isPlaying() { return this.#isPlaying; }
    get isRecording() { return this.#isRecording; }
    get isCountingIn() { return this.#isCountingIn; }
    get countInBeatsRemaining() { return this.#countInBeatsRemaining; }
    get position() { return this.#position; }
    get bpm() { return this.#bpm; }
    get playbackTimestamp() { return this.#playbackTimestamp; }
    get markerState() { return this.#markerState; }
    get project() { return this.#project; }
    get preferences() { return this.#preferences; }
    get cpuLoad() { return this.#cpuLoad; }
    get perfBuffer() { return this.#perfBuffer; }
    get perfIndex() { return this.#perfIndex; }
    isReady() { return this.#isReady; }
    queryLoadingComplete() { return this.#commands.queryLoadingComplete(); }
    noteSignal(signal) { this.#commands.noteSignal(signal); }
    subscribeNotes(observer) { return this.#notifyNoteSignals.subscribe(observer); }
    ignoreNoteRegion(uuid) { this.#commands.ignoreNoteRegion(uuid); }
    suspendAutomation(uuid) { this.#commands.suspendAutomation(uuid); }
    scheduleClipPlay(clipIds) {
        this.#notifyClipNotification.notify({ type: "waiting", clips: clipIds });
        this.#commands.scheduleClipPlay(clipIds);
    }
    scheduleClipStop(trackIds) {
        this.#commands.scheduleClipStop(trackIds);
    }
    subscribeClipNotification(observer) {
        observer({
            type: "sequencing",
            changes: { started: this.#playingClips, stopped: Arrays.empty(), obsolete: Arrays.empty() }
        });
        return this.#notifyClipNotification.subscribe(observer);
    }
    subscribeDeviceMessage(uuid, listener) {
        this.#deviceMessageListeners.add(uuid, listener);
        return { terminate: () => this.#deviceMessageListeners.remove(uuid, listener) };
    }
    registerMonitoringSource(uuid, node, numChannels, destinationNode) {
        this.#monitoringRouter.registerSource(uuid, node, numChannels, destinationNode);
    }
    unregisterMonitoringSource(uuid) {
        this.#monitoringRouter.unregisterSource(uuid);
    }
    #updateCpuLoad(budgetMs, project) {
        while (this.#lastPerfReadIndex !== this.#perfIndex) {
            const ms = this.#perfBuffer[this.#lastPerfReadIndex];
            if (ms > this.#maxMsSinceLastUpdate) {
                this.#maxMsSinceLastUpdate = ms;
            }
            if (ms >= budgetMs) {
                this.#consecutiveOverloadCount++;
                if (this.#consecutiveOverloadCount >= 30) {
                    project.handleCpuOverload();
                    this.#consecutiveOverloadCount = 0;
                }
            }
            else {
                this.#consecutiveOverloadCount = 0;
            }
            this.#lastPerfReadIndex = (this.#lastPerfReadIndex + 1) % PERF_BUFFER_SIZE;
        }
        const now = performance.now();
        if (now - this.#lastCpuLoadUpdate >= 1000) {
            this.#cpuLoad.setValue(Math.round((this.#maxMsSinceLastUpdate / budgetMs) * 100));
            this.#maxMsSinceLastUpdate = 0;
            this.#lastCpuLoadUpdate = now;
        }
    }
    terminate() {
        this.#commands.terminate();
        this.#terminator.terminate();
        this.disconnect();
    }
}
